import { useEffect, useMemo } from 'react';
import { useLoaderData } from 'react-router';

import {
    classifyDownload,
    groupDownloadsByCategory,
} from '@modules/product/util/classify-download';
import { useGatedDownloads } from '@modules/product/hook/use-gated-downloads';
import FileRow from '@modules/product/component/downloads/FileRow';
import {
    ARCHIPRO_SPEC_SHEET_ID,
    ARCHIPRO_SPEC_SHEET_PROFESSIONAL,
} from '@modules/product/tear-sheet/archipro-spec-sheet';
import type { loader } from '~/routes/_app.product.$urlSegment';

/** Products warmed this session, so we fire the size-warming HEAD at most once. */
const warmedTearSheets = new Set<number>();

/**
 * (03) Downloads & Documentation — the product files inline in the accordion,
 * carrying the legacy `ProductFilesModal`'s per-file tier gating (Safety free,
 * Specs creates a Lead once, ProDesign needs sign-in) and the same
 * `DownloadsClick` tracking via the shared `FileRow`. The whole row is the
 * download control here, so there is no separate per-file enquire shortcut;
 * the files modal keeps its own. No reopen intent is needed either — after
 * sign-in the page reloads and the list is simply present.
 */
export const DownloadsRowContent = () => {
    const { product } = useLoaderData<typeof loader>();
    const professional = product.Professional;
    const { isLoggedIn, specsGated, handleGatedDownload } = useGatedDownloads({
        productID: product.ID,
        professionalID: professional.ID,
        productImage:
            product.LibraryImages?.[0]?.LibraryImageFile?.DetailPageSquare
                ?.Desktop ?? undefined,
    });

    const productThumbnail =
        product.LibraryImages?.[0]?.LibraryImageFile?.DetailPageSquare
            ?.Desktop ?? undefined;

    // First view of a product whose tear-sheet size isn't cached yet: silently
    // HEAD the PDF route so it renders once and records the real size. No body
    // is transferred; the size then shows from the DB on the next load. Skipped
    // entirely once a size exists (Product.TearSheetSizeBytes is set).
    useEffect(() => {
        if (product.TearSheetSizeBytes != null) return;
        if (warmedTearSheets.has(product.ID)) return;
        warmedTearSheets.add(product.ID);
        void fetch(`${product.Link}/tearsheet.pdf`, {
            method: 'HEAD',
        }).catch(() => undefined);
    }, [product.ID, product.Link, product.TearSheetSizeBytes]);

    const validDownloads = useMemo(
        () =>
            (product.LibraryDownloads ?? []).filter(
                (download) => download.InternalLink || download.ExternalLink,
            ),
        [product.LibraryDownloads],
    );

    // The ArchiPro spec sheet is injected into LibraryDownloads server-side (in
    // the product loader), so it already appears as the first item of the Spec
    // Sheets group here.
    const categories = useMemo(
        () => groupDownloadsByCategory(validDownloads),
        [validDownloads],
    );

    return (
        <div className="flex flex-col gap-10 pb-[1.8rem] max-md:gap-8 max-md:pb-4">
            {categories.map((cat) => (
                <div key={cat.categoryId}>
                    <h3 className="mb-3 font-sans leading-tight font-medium text-[#1f1f21] max-md:mb-1 max-md:text-[0.9375rem] max-md:tracking-[-0.01em] max-md:text-[#767676] md:px-1 md:text-[1.375rem]">
                        {cat.categoryTitle}
                    </h3>
                    <div className="md:space-y-[0.1875rem]">
                        {cat.downloads.map((download) => {
                            const isTearSheet =
                                download.ID === ARCHIPRO_SPEC_SHEET_ID;
                            const tier = classifyDownload(download);
                            const isFileSpecsGated =
                                tier === 'Specs' && specsGated;
                            const isProDesignGated =
                                tier === 'ProDesign' && !isLoggedIn;
                            const isGated =
                                isFileSpecsGated || isProDesignGated;
                            const link =
                                download.ExternalLink ||
                                download.InternalLink ||
                                '';

                            return (
                                <FileRow
                                    key={download.ID}
                                    download={download}
                                    group={tier}
                                    productID={product.ID}
                                    professionalID={product.Professional.ID}
                                    professionalTitle={
                                        isTearSheet
                                            ? ARCHIPRO_SPEC_SHEET_PROFESSIONAL
                                            : product.Professional.Title
                                    }
                                    isLoggedIn={isLoggedIn}
                                    isGated={isGated}
                                    theme="light"
                                    thumbnailSrc={productThumbnail}
                                    onGatedDownload={
                                        isGated
                                            ? () =>
                                                  handleGatedDownload(
                                                      link,
                                                      isFileSpecsGated,
                                                  )
                                            : undefined
                                    }
                                />
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
};
